// @flow
import React from 'react'
import isMobile from 'is-mobile'
import { equals } from 'ramda'

import AbstractFormField from '../../Core/Components/_formFields/AbstractFormField'
import { HTMLInputTypes } from '../../Core/Enums'
import { capitalizeFirstLetter } from '../../Core/InputHelpFunctions'
import { sortObjectArrayByList, sortObjectArrayByProp } from '../../Core/SortFunctions'

import type { ResourcesType } from '../../Core/Types/Resources'
import type { DefaultFormFieldState, DefaultFormFieldType } from '../../Core/Components/_formFields/AbstractFormField'

import Html5Select from './SelectEnumRefIndex/Html5Select'
import ReactSelect from './SelectEnumRefIndex/ReactSelect'
import ReactSelectOption from './SelectEnumRefIndex/ReactSelect/Option'

export type SelectItem = {
  [key: string]: string,
}

type PreferredLimits = {
  [key: string]: number,
}

type ValueNames = {
  [key: string]: string,
}

export type SelectEnumRefIndexProps = DefaultFormFieldType & {
  domain: {
    _type: string,
  },
  label: string,
  preferredLimits?: PreferredLimits,
  preferredValues?: Array<string>,
  resources: ResourcesType,
  value: string,
}

export type SelectEnumRefIndexState = DefaultFormFieldState & {
  label: string,
  oldDependeeValue: string,
  oldValue: string,
  oldValueNames: ValueNames,
  options?: Array<SelectItem>,
  value: string,
}

const MOBILE_CONTENT_KEY: string = 'mobile'
const DESKTOP_CONTENT_KEY: string = 'desktop'

const DEFAULT_PREFERRED_OPTIONS_LIMIT: number = 10

export default class SelectEnumRefIndex extends AbstractFormField<SelectEnumRefIndexProps, SelectEnumRefIndexState> {
  static defaultProps = {
    inputType: HTMLInputTypes.TEXT,
  }

  static getDerivedStateFromProps(nextProps: SelectEnumRefIndexProps, prevState: SelectEnumRefIndexState) {
    return getDerivedStateFromPropsLogic(nextProps, prevState, getSelectOptions)
  }

  constructor(props: SelectEnumRefIndexProps) {
    super(props)

    this.state = {
      ...this.state,
      oldDependeeValue: '',
      oldValue: '',
      oldValueNames: props.valueNames,
      options: [],
      preferredLimits: {},
      preferredValues: [],
      value: props.value || '',
    }
  }

  handleOnUpdateValue = (value: string) => {
    this.setState({
      value,
    })

    this.props.onChange(value)
  }

  render() {
    return <div>{
      isMobile() ? this.renderHtml5Select() : this.renderReactSelect()}
    </div>
  }

  renderHtml5Select = (): Object => {
    return (
      <Html5Select
        error={this.renderError()}
        id={this.props.id}
        name={this.props.name}
        onUpdate={this.handleOnUpdateValue}
        options={this.state.options}
        value={this.state.value}
      />
    )
  }

  renderReactSelect = (): Object => {
    return (
      <ReactSelect
        error={this.renderError()}
        name={this.props.name}
        onUpdate={this.handleOnUpdateValue}
        optionComponent={ReactSelectOption}
        options={this.state.options}
        value={this.state.value}
      />
    )
  }

  renderError = (): Object => {
    return this.hasErrors() ? this.renderErrorHtml() : null
  }
}

export const getDerivedStateFromPropsLogic = (
  nextProps: SelectEnumRefIndexProps,
  prevState: SelectEnumRefIndexState,
  getSelectOptions: Function,
): SelectEnumRefIndexState => {
  const newState = {}
  let isChanged = false

  if (hasAvailableDependeeResources(nextProps)) {
    if (hasDependeeFieldChanged(nextProps, prevState)) {
      newState.oldDependeeValue = getDependeeValue(nextProps)
      isChanged = true
    }

    if (haveValueNamesLoaded(nextProps, prevState)) {
      newState.oldValueNames = nextProps.valueNames
      isChanged = true
    }

    if (isChanged) {
      newState.options = getSelectOptions(nextProps)
    }
  }

  if (hasInputValueChanged(nextProps, prevState)) {
    newState.value = nextProps.value
    newState.oldValue = nextProps.value

    isChanged = true
  }

  return isChanged ? newState : null
}

export const hasAvailableDependeeResources = (nextProps: SelectEnumRefIndexProps): boolean => {
  return !!(
    nextProps.resources &&
    nextProps.resources[nextProps.domain.resourceName] &&
    nextProps.formData[nextProps.domain.dependeeFieldPath]
  )
}

export const hasDependeeFieldChanged = (
  nextProps: SelectEnumRefIndexProps,
  prevState: SelectEnumRefIndexState,
): boolean => {
  const dependeeValue = getDependeeValue(nextProps)

  return dependeeValue !== undefined && dependeeValue !== prevState.oldDependeeValue
}

export const haveValueNamesLoaded = (
  nextProps: SelectEnumRefIndexProps,
  prevState: SelectEnumRefIndexState,
): boolean => {
  return nextProps.valueNames !== undefined && !equals(nextProps.valueNames, prevState.oldValueNames)
}

export const hasInputValueChanged = (
  nextProps: SelectEnumRefIndexProps,
  prevState: SelectEnumRefIndexState,
): boolean => {
  return nextProps.value !== undefined && nextProps.value !== prevState.oldValue
}

export const getSelectOptions = (nextProps: SelectEnumRefIndexProps): Array<SelectItem> => {
  return addPreferredOptions(nextProps, getDefaultOptions(nextProps), getPreferredValues(nextProps))
}

export const getDefaultOptions = (nextProps: SelectEnumRefIndexProps): Array<SelectItem> => {
  const dependeeValue = getDependeeValue(nextProps)

  if (!nextProps.resources[nextProps.domain.resourceName][dependeeValue]) {
    return []
  }

  const options = nextProps.resources[nextProps.domain.resourceName][dependeeValue].map((value) => {
    return {
      label: getOptionLabel(nextProps, value),
      value: value,
    }
  })

  return sortObjectArrayByProp([...options], 'label')
}

export const getOptionLabel = (nextProps: SelectEnumRefIndexProps, value: string): string => {
  const valueName = nextProps.valueNames ? nextProps.valueNames[value] : undefined

  return valueName ? valueName : capitalizeFirstLetter(value)
}

export const getDependeeValue = (nextProps: SelectEnumRefIndexProps): string => {
  if (!nextProps.formData || !nextProps.domain || !nextProps.domain.dependeeFieldPath) {
    return ''
  }

  return nextProps.formData[nextProps.domain.dependeeFieldPath]
}

export const addPreferredOptions = (
  nextProps: SelectEnumRefIndexProps,
  defaultOptions: Array<SelectItem>,
  preferredValues: Array<string>,
): Array<SelectItem> => {
  if (preferredValues.length === 0) {
    return defaultOptions
  }

  return [...getPreferredOptions(defaultOptions, preferredValues), ...defaultOptions]
}

export const getPreferredValues = (nextProps: SelectEnumRefIndexProps): Array<string> => {
  const dependeeValue = getDependeeValue(nextProps)

  if (!dependeeValue || !nextProps.preferredValues || !nextProps.preferredValues[dependeeValue]) {
    return []
  }

  return applyPreferredValuesLimit(nextProps, nextProps.preferredValues[dependeeValue])
}

export const applyPreferredValuesLimit = (
  nextProps: SelectEnumRefIndexProps,
  preferredValues: Array<string>,
): Array<string> => {
  const platformKey = isMobile() ? MOBILE_CONTENT_KEY : DESKTOP_CONTENT_KEY

  return preferredValues.slice(0, getPreferredValuesLimit(nextProps, platformKey))
}

export const getPreferredValuesLimit = (nextProps: SelectEnumRefIndexProps, platformKey: string): number => {
  if (!nextProps.preferredLimits || !nextProps.preferredLimits[platformKey]) {
    return DEFAULT_PREFERRED_OPTIONS_LIMIT
  }

  return nextProps.preferredLimits[platformKey]
}

export const getPreferredOptions = (
  defaultOptions: Array<SelectItem>,
  preferredValues: Array<string>,
): Array<SelectItem> => {
  const preferredOptions = sortObjectArrayByList(
    defaultOptions.filter((option) => {
      return preferredValues.includes(option.value)
    }),
    'value',
    preferredValues,
  )

  return preferredOptions.map((option, index) => {
    return {
      ...option,
      preferred: {
        index,
        count: preferredOptions.length,
      },
    }
  })
}
